import { Alert, LTSSService, FinancialSummary } from '../types/ltss';

const WEBHOOK_CONFIG_KEY = 'ltss-webhook-config';

export interface WebhookConfig {
  url: string;
  enabled: boolean;
  triggerOn: {
    critical: boolean;
    warning: boolean;
    caution: boolean;
    info: boolean;
  };
  includeDetails: boolean;
  lastTriggered: Date | null;
}

export interface WebhookPayload {
  timestamp: string;
  source: 'ltss-analytics-dashboard';
  event: 'alerts_detected' | 'report_uploaded';
  fileName: string;
  summary: {
    totalServices: number;
    totalAlerts: number;
    criticalAlerts: number;
    warningAlerts: number;
    totalRevenue: number;
    staffCount: number;
    clientCount: number;
  };
  alerts: {
    id: string;
    type: string;
    category: string;
    message: string;
    staffName?: string;
    clientName?: string;
  }[];
  metadata?: {
    weekRange: string;
    uploadDate: string;
  };
}

const defaultConfig: WebhookConfig = {
  url: '',
  enabled: false,
  triggerOn: {
    critical: true,
    warning: true,
    caution: false,
    info: false,
  },
  includeDetails: true,
  lastTriggered: null,
};

export function getWebhookConfig(): WebhookConfig {
  try {
    const stored = localStorage.getItem(WEBHOOK_CONFIG_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultConfig, ...parsed };
    }
  } catch (e) {
    console.error('Failed to load webhook config:', e);
  }
  return defaultConfig;
}

export function saveWebhookConfig(config: WebhookConfig): void {
  try {
    localStorage.setItem(WEBHOOK_CONFIG_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save webhook config:', e);
  }
}

export function buildWebhookPayload(
  fileName: string,
  services: LTSSService[],
  alerts: Alert[],
  financials: FinancialSummary,
  staffCount: number,
  clientCount: number,
  config: WebhookConfig
): WebhookPayload {
  // Filter alerts based on config
  const filteredAlerts = alerts.filter(alert => {
    if (alert.type === 'CRITICAL' && config.triggerOn.critical) return true;
    if (alert.type === 'WARNING' && config.triggerOn.warning) return true;
    if (alert.type === 'CAUTION' && config.triggerOn.caution) return true;
    if (alert.type === 'INFO' && config.triggerOn.info) return true;
    return false;
  });

  // Get week range from services
  const weeks = Array.from(new Set(services.map(s => s.week).filter(Boolean)));
  weeks.sort();
  const weekRange = weeks.length > 0 
    ? (weeks.length === 1 ? weeks[0] : `${weeks[0]} - ${weeks[weeks.length - 1]}`)
    : 'Unknown';

  return {
    timestamp: new Date().toISOString(),
    source: 'ltss-analytics-dashboard',
    event: 'alerts_detected',
    fileName,
    summary: {
      totalServices: services.length,
      totalAlerts: alerts.length,
      criticalAlerts: alerts.filter(a => a.type === 'CRITICAL').length,
      warningAlerts: alerts.filter(a => a.type === 'WARNING').length,
      totalRevenue: financials.totalAmountPaid,
      staffCount,
      clientCount,
    },
    alerts: config.includeDetails 
      ? filteredAlerts.map(a => ({
          id: a.id,
          type: a.type,
          category: a.category,
          message: a.message,
          staffName: a.staffName,
          clientName: a.clientName,
        }))
      : [],
    metadata: {
      weekRange,
      uploadDate: new Date().toISOString(),
    },
  };
}

export async function sendWebhook(
  payload: WebhookPayload,
  webhookUrl: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return { 
        success: false, 
        error: `HTTP ${response.status}: ${response.statusText}` 
      };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Network error' 
    };
  }
}

export function shouldTriggerWebhook(alerts: Alert[], config: WebhookConfig): boolean {
  if (!config.enabled || !config.url) return false;

  return alerts.some(alert => {
    if (alert.type === 'CRITICAL' && config.triggerOn.critical) return true;
    if (alert.type === 'WARNING' && config.triggerOn.warning) return true;
    if (alert.type === 'CAUTION' && config.triggerOn.caution) return true;
    if (alert.type === 'INFO' && config.triggerOn.info) return true;
    return false;
  });
}
